import React, { useState } from 'react';
import { CalendarRange, Gauge } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useAiJob } from '../../hooks/useAiJob';
import { worldTabs, tabLabel } from './worldTabs';
import {
  Box, Frame, Row, Button, Muted, ErrorBox,
} from './adminStyles';

const MIN_SCENARIOS = 2;

/** One scenario's course: its status, run again against the stored reading, and its effect analysis. */
function WorldRow({ instanceId, runId, tab, locked, reload }) {
  const { t } = useTranslation();
  const sim = useAiJob({ type: 'future.simulate', dedupeKey: `future.simulate:${runId}:${tab.worldId}` });
  const effect = useAiJob({ type: 'future.effect', dedupeKey: `future.effect:${tab.id}` });
  const [error, setError] = useState(null);
  const status = tab.timeline.status;
  const running = ['queued', 'running'].includes(sim.status) || status === 'running';
  const analysing = ['queued', 'running'].includes(effect.status);
  const act = async (job, input) => {
    setError(null);
    try { await job.start({ instanceId, runId, ...input }); } catch (e) { setError(e.message); } finally { reload(); }
  };
  return (
    <Frame>
      <Row>
        <strong title={tab.title}>{tabLabel(tab, 48)}</strong>
        <small>{t(`futureReimagined.worldAdmin.status.${running ? 'running' : status}`)}{tab.timeline.stale ? ` · ${t('futureReimagined.scenario.stale')}` : ''}</small>
      </Row>
      <Row>
        <Button type="button" onClick={() => act(sim, { worldId: tab.worldId })} disabled={locked || running || tab.timeline.stale}>
          <CalendarRange size={14} aria-hidden />{t('futureReimagined.worldAdmin.rerun')}
        </Button>
        {status === 'done' && (
          <Button type="button" onClick={() => act(effect, { timelineId: tab.id })} disabled={locked || analysing}>
            <Gauge size={14} aria-hidden />{t(analysing ? 'futureReimagined.worldAdmin.analysing' : 'futureReimagined.worldAdmin.analyse')}
          </Button>
        )}
      </Row>
      {(error || sim.status === 'failed' || status === 'failed') && (
        <ErrorBox role="alert">{t('futureReimagined.course.failed', { message: error || String(sim.error || tab.timeline.error || '') })}</ErrorBox>
      )}
      {effect.status === 'failed' && <ErrorBox role="alert">{t('futureReimagined.effect.failed', { message: String(effect.error || '') })}</ErrorBox>}
    </Frame>
  );
}

/**
 * The facilitator's scenarios (#1062): which source is in use and how many it gives,
 * the reading's status, and per scenario a re-run against the stored reading and
 * the effect analysis. Reading them all and imagining a course in each is the
 * module's admin action "simulate" in the section's action shelf
 * (specs/facilitator-console.md); this shows what it produced.
 */
export default function WorldRuns({ instanceId, state, locked, reload }) {
  const { t } = useTranslation();
  const scenarios = state.scenarios || { list: [] };
  const tabs = worldTabs(state.timelines || []);
  const enough = scenarios.list.length >= MIN_SCENARIOS;
  return (
    <Box>
      <h4>{t('futureReimagined.worldAdmin.title')}</h4>
      <Muted>
        {enough
          ? t('futureReimagined.worldAdmin.source', { source: t(`futureReimagined.scenario.sources.${scenarios.source}`), count: scenarios.list.length })
          : t('futureReimagined.worldAdmin.none', { count: scenarios.list.length, min: MIN_SCENARIOS })}
        {scenarios.placeholder && scenarios.source === 'expert' ? ` ${t('futureReimagined.scenario.placeholder')}` : ''}
      </Muted>
      {state.reading && <Muted>{t(`futureReimagined.worldAdmin.readingStatus.${state.reading.status}`)}</Muted>}
      {state.reading?.status === 'failed' && <ErrorBox role="alert">{t('futureReimagined.reading.failed', { message: state.reading.error || '' })}</ErrorBox>}
      {tabs.map((tab) => <WorldRow key={tab.id} instanceId={instanceId} runId={state.run.id} tab={tab} locked={locked} reload={reload} />)}
    </Box>
  );
}
